/**
 * SQ-P3 — Estimate revision reconciliation workspace (full-screen for large change sets).
 */
import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Modal,
  Row,
  Segmented,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { formatMoney, formatPercent } from '@/utils/format';
import quotationService, {
  type EstimateReconciliationPreview,
  type ReconciliationDecision,
} from '@/services/quotation.service';
import { useMediaQuery } from '@/hooks/useMediaQuery';

const { Title, Text } = Typography;

type FilterKey =
  | 'needs_decision'
  | 'all'
  | 'added'
  | 'removed'
  | 'quantity'
  | 'cost'
  | 'description'
  | 'override';

const decisionOptions: ReconciliationDecision[] = [
  'ACCEPT_ESTIMATE_CHANGE',
  'KEEP_QUOTATION_VALUE',
  'ADD',
  'IGNORE',
  'REMOVE',
  'REVIEW',
];

export function QuotationEstimateReconciliationWorkspace() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isTablet = useMediaQuery('(max-width: 1180px)');

  const params = new URLSearchParams(window.location.search);
  const targetEstimationId = params.get('targetEstimationId') || '';

  const [filter, setFilter] = useState<FilterKey>('needs_decision');
  const [decisions, setDecisions] = useState<Record<string, ReconciliationDecision>>({});

  const { data: preview, isLoading, refetch } = useQuery({
    queryKey: ['estimate-reconciliation', id, targetEstimationId, decisions],
    queryFn: () =>
      quotationService.previewEstimateReconciliation(id!, {
        targetEstimationId,
        decisions: Object.entries(decisions).map(([change_id, decision]) => ({
          change_id,
          decision,
        })),
      }),
    enabled: !!id && !!targetEstimationId,
  });

  const applyMutation = useMutation({
    mutationFn: () =>
      quotationService.applyEstimateReconciliation(id!, {
        targetEstimationId,
        previewHash: preview!.preview_hash,
        expectedQuotationUpdatedAt: preview!.quotation_updated_at,
        decisions: Object.entries(decisions).map(([change_id, decision]) => ({
          change_id,
          decision,
        })),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotation', id] });
      navigate(`/growth/quotations/${id}`);
    },
  });

  const filteredChanges = useMemo(() => {
    if (!preview) return [];
    const rows = preview.changes;
    switch (filter) {
      case 'needs_decision':
        return rows.filter((r) => r.requires_decision);
      case 'added':
        return rows.filter((r) => r.change_type === 'ADDED');
      case 'removed':
        return rows.filter((r) => r.change_type === 'REMOVED');
      case 'quantity':
        return rows.filter((r) => r.change_type === 'QUANTITY_CHANGED');
      case 'cost':
        return rows.filter((r) => r.change_type === 'COST_CHANGED');
      case 'description':
        return rows.filter(
          (r) => r.change_type === 'DESCRIPTION_CHANGED' || r.change_type === 'SPECIFICATION_CHANGED'
        );
      case 'override':
        return rows.filter((r) => r.commercially_modified || r.line_provenance === 'COMMERCIAL_OVERRIDE');
      default:
        return rows;
    }
  }, [preview, filter]);

  const resolvedCount = useMemo(() => {
    if (!preview) return 0;
    return preview.changes.filter((c) => !c.requires_decision || decisions[c.change_id]).length;
  }, [preview, decisions]);

  const columns: ColumnsType<EstimateReconciliationPreview['changes'][0]> = [
    {
      title: 'Item',
      key: 'item',
      render: (_, row) =>
        row.estimate_change.current_line?.item_description ||
        row.estimate_change.previous_line?.item_description ||
        row.change_id,
    },
    {
      title: 'Change',
      dataIndex: 'change_type',
      render: (v: string) => <Tag>{v.replace(/_/g, ' ')}</Tag>,
    },
    {
      title: 'Decision',
      key: 'decision',
      render: (_, row) => (
        <Segmented
          size="small"
          options={row.allowed_decisions.map((d) => ({ label: d.replace(/_/g, ' '), value: d }))}
          value={decisions[row.change_id] ?? row.default_decision}
          onChange={(v) =>
            setDecisions((prev) => ({ ...prev, [row.change_id]: v as ReconciliationDecision }))
          }
        />
      ),
    },
  ];

  const onApply = () => {
    Modal.confirm({
      title: `Apply ${preview?.target_estimate.estimation_number} REV ${preview?.target_estimate.revision_number}?`,
      content: (
        <Space direction="vertical">
          <Text>
            {preview?.estimate_comparison.summary.added_count} lines will be added ·{' '}
            {preview?.estimate_comparison.summary.removed_count} removed
          </Text>
          <Text type="secondary">Projected margin: {formatPercent(preview?.commercial_impact.projected_margin_percent)}</Text>
        </Space>
      ),
      onOk: () => applyMutation.mutateAsync(),
    });
  };

  if (!targetEstimationId) {
    return <Alert type="error" message="targetEstimationId query parameter is required" />;
  }

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Space align="center" style={{ justifyContent: 'space-between', width: '100%' }}>
          <Title level={4} style={{ margin: 0 }}>
            Estimate Revision Reconciliation
          </Title>
          <Button onClick={() => navigate(`/growth/quotations/${id}`)}>Back to quotation</Button>
        </Space>

        {preview?.newer_estimate_available ? (
          <Alert
            type="warning"
            message="A newer estimate revision is now available"
            description={`Current reconciliation: REV ${preview.target_estimate.revision_number}. Latest approved: REV ${preview.newer_estimate_available.revision_number}.`}
          />
        ) : null}

        {preview?.warnings?.map((w) => (
          <Alert key={w} type="warning" message={w} />
        ))}

        <Card loading={isLoading}>
          <Row gutter={16}>
            <Col xs={24} md={6}>
              <Statistic title="Current cost" value={formatMoney(preview?.commercial_impact.current_cost)} />
            </Col>
            <Col xs={24} md={6}>
              <Statistic title="Projected cost" value={formatMoney(preview?.commercial_impact.projected_cost)} />
            </Col>
            <Col xs={24} md={6}>
              <Statistic title="Sell (preserved)" value={formatMoney(preview?.commercial_impact.projected_sell)} />
            </Col>
            <Col xs={24} md={6}>
              <Statistic
                title="Projected margin %"
                value={formatPercent(preview?.commercial_impact.projected_margin_percent)}
              />
            </Col>
          </Row>
        </Card>

        <Card
          title={
            <Space>
              <Text>
                {preview?.changes.length ?? 0} changes · {resolvedCount} resolved ·{' '}
                {preview?.unresolved_decisions ?? 0} need decision
              </Text>
            </Space>
          }
          extra={
            <Segmented
              value={filter}
              onChange={(v) => setFilter(v as FilterKey)}
              options={[
                { label: 'Needs decision', value: 'needs_decision' },
                { label: 'All', value: 'all' },
                { label: 'Added', value: 'added' },
                { label: 'Removed', value: 'removed' },
                { label: 'Qty', value: 'quantity' },
                { label: 'Cost', value: 'cost' },
              ]}
            />
          }
        >
          {isTablet ? (
            <Space direction="vertical" style={{ width: '100%' }}>
              {filteredChanges.map((row) => (
                <Card key={row.change_id} size="small" title={row.change_type.replace(/_/g, ' ')}>
                  <Text>
                    {row.estimate_change.current_line?.item_description ||
                      row.estimate_change.previous_line?.item_description}
                  </Text>
                  <Segmented
                    block
                    options={row.allowed_decisions.map((d) => ({ label: d.replace(/_/g, ' '), value: d }))}
                    value={decisions[row.change_id] ?? row.default_decision}
                    onChange={(v) =>
                      setDecisions((prev) => ({ ...prev, [row.change_id]: v as ReconciliationDecision }))
                    }
                    style={{ marginTop: 8 }}
                  />
                </Card>
              ))}
            </Space>
          ) : (
            <Table rowKey="change_id" dataSource={filteredChanges} columns={columns} pagination={{ pageSize: 20 }} />
          )}
        </Card>

        <Space>
          <Button onClick={() => refetch()}>Refresh preview</Button>
          <Button
            type="primary"
            disabled={(preview?.unresolved_decisions ?? 0) > 0 || applyMutation.isPending}
            loading={applyMutation.isPending}
            onClick={onApply}
          >
            Apply Changes
          </Button>
        </Space>
      </Space>
    </div>
  );
}
